import { z as zod } from "zod";

/**
 * The DTO's own rule, copied exactly: `@Matches(/^(?!0+(\.0+)?$)\d{1,8}(\.\d{1,4})?$/)` on
 * `UpsertPriceBookItemRequest.amount`. Zero is refused there and has to be refused here too — a
 * stored 0.0000 resolves as a perfectly good rate and quotes the work free.
 *
 * Amount stays a string from the input to the wire. The column is numeric(12,4) and the API
 * rejects a JSON number outright — money that round-trips through a float is money that is
 * quietly wrong, and a rate is multiplied by thousands of minutes before anyone reads the total.
 */
export const AMOUNT_PATTERN = /^(?!0+(\.0+)?$)\d{1,8}(\.\d{1,4})?$/;

/**
 * The four fields every rate form edits, shared so the inline row and the drawer cannot validate
 * the same value two different ways.
 */
export const rateSchemaShape = {
	amount: zod
		.string()
		.min(1, "An amount is required")
		.regex(AMOUNT_PATTERN, "A positive decimal, at most 4 decimal places — e.g. 1200.0000"),
	unit: zod.enum(["each", "per_month", "per_minute"]),
	currency: zod
		.string()
		.trim()
		// Upper-cased before the check rather than refused for it: "sgd" is the code, typed quickly.
		.transform((value) => value.toUpperCase())
		.pipe(zod.string().regex(/^[A-Z]{3}$/, "A three-letter ISO 4217 code, e.g. SGD")),
	note: zod.string().max(255, "255 characters at most"),
};

export const rateSchema = zod.object(rateSchemaShape);

export type RateFormValues = zod.infer<typeof rateSchema>;
